// Browser-owned retry correlation for Task-thread POSTs. The pending id survives only an uncertain
// transport outcome and may be reused only for the exact same Task path and message. It is never authority.

import { mintTransportRequestId } from './transport-request-id.ts';

export interface PendingTaskRequest {
  readonly requestId: string;
  readonly taskId: string;
  readonly message: string;
}

/** Minimum page-memory needed to make an uncertain Task POST retry exact; never authorization. */
export interface TaskComposerRetention {
  readonly draft: string;
  readonly pendingRequest: PendingTaskRequest | null;
  readonly status: 'draft' | 'in_flight' | 'uncertain';
}

/** Controller events reduced by the always-mounted Task draft registry. */
export type TaskComposerRetentionEvent =
  | { readonly kind: 'draft_changed'; readonly draft: string }
  | {
      readonly kind: 'request_started';
      readonly draft: string;
      readonly pendingRequest: PendingTaskRequest;
    }
  | {
      readonly kind: 'request_accepted' | 'request_unknown' | 'request_refused';
      readonly pendingRequest: PendingTaskRequest;
    };

export function mintTaskRequest(taskId: string, message: string): PendingTaskRequest {
  return { requestId: mintTransportRequestId(), taskId, message };
}

/** Only an unconfirmed exact Task/message retry may reuse the uncertain request identity. */
export function reusablePendingTaskRequest(
  pending: PendingTaskRequest | null,
  taskId: string,
  message: string,
): PendingTaskRequest | undefined {
  return pending?.taskId === taskId && pending.message === message ? pending : undefined;
}
